/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";

import { format } from "date-fns";
import { formatDistance } from "date-fns/formatDistance";
import { debounce } from "lodash";
import { FiSearch } from "react-icons/fi";

import { Paginated } from "@/jenesys-types/data/common";
import { IChartOfAccounts } from "@/jenesys-types/jenesys-entities/config";

import { Paginate } from "@/components/common/table/Paginate";
import { MoreInfo } from "@/components/routes/company/MoreInfo";
import { addTimeZoneToTimestamp } from "@/util/helpers";

interface ChartOfAccountsProps {
  coaPage: number;
  isRefreshing: boolean;
  chartOfAccounts: Paginated<IChartOfAccounts>;
  isLoadingCOAs: boolean;
  setCOAPage: (value: number) => void;
  handleRefresh: () => void;
  setSearchQuery: (value: string) => void;
  handleCOAPageClick: (selected: any) => void;
  isConnected: boolean;
}

export const ChartOfAccounts: React.FC<ChartOfAccountsProps> = ({
  coaPage,
  isRefreshing,
  isConnected,
  isLoadingCOAs,
  chartOfAccounts,
  setCOAPage,
  handleRefresh,
  setSearchQuery,
  handleCOAPageClick
}) => {
  const debouncedOnChange = useCallback(
    debounce((value: string) => {
      setCOAPage(0);
      setSearchQuery(value);
    }, 500),
    []
  );

  return (
    <Accordion mt={12} defaultIndex={[0]} allowMultiple>
      <AccordionItem border={0}>
        <h2>
          <AccordionButton>
            <HStack textAlign="left" flex={1}>
              <Text fontWeight={600}>Chart of Accounts</Text>

              <MoreInfo
                placement="right"
                text="Jack fetches the Assets, Liabilities, Expenses and Revenue Account Types. Jack does not fetch Equity or Archived Accounts."
              />
            </HStack>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <Box>
            <TableContainer
              border="1px solid"
              borderColor="blackAlpha.300"
              rounded={6}
              pt={6}
              pb={6}
              px={8}
              mt={4}
              flexShrink={0}
            >
              <HStack justify="space-between" mb={10}>
                <InputGroup
                  w="full"
                  maxW={456}
                  borderColor="blackAlpha.300"
                  h={35}
                >
                  <InputLeftElement top={-0.5} pointerEvents="none">
                    <FiSearch fontSize={16} color="gray" />
                  </InputLeftElement>
                  <Input
                    onChange={(event) => {
                      debouncedOnChange(event.target.value);
                    }}
                    fontSize={14}
                    h={35}
                    type="search"
                    placeholder="Search account codes..."
                  />
                </InputGroup>
                {isConnected && (
                  <HStack>
                    <Button
                      onClick={handleRefresh}
                      isLoading={isRefreshing}
                      size="sm"
                      variant="outline"
                    >
                      Refresh
                    </Button>
                    <MoreInfo
                      placement="top"
                      text="Refreshing allows Jack to fetch the most recent Chart of Accounts and Tax Codes for your company"
                    />
                  </HStack>
                )}
              </HStack>

              <Table variant="unstyled" size="sm">
                <Thead>
                  <Tr color="black">
                    <Th
                      pl={0}
                      textTransform="capitalize"
                      fontWeight={600}
                      fontSize={14}
                    >
                      Type
                    </Th>
                    <Th
                      fontSize={14}
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      Account code
                    </Th>
                    <Th
                      fontSize={14}
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      Account name
                    </Th>
                    <Th
                      fontSize={14}
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      Tax Code
                    </Th>

                    <Th
                      color="black"
                      fontSize={14}
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      Last fetched
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {chartOfAccounts?.items?.map((account: any) => (
                    <Tr key={account.id}>
                      <Td pl={0} fontWeight={500} py={4}>
                        {account.type}
                      </Td>
                      <Td fontWeight={500} py={4}>
                        {account.code}
                      </Td>
                      <Td fontWeight={500} py={4}>
                        {account.name}
                      </Td>
                      <Td fontWeight={500} py={4}>
                        {account.tax_code?.name}
                      </Td>
                      {account.updated_at && (
                        <Td fontWeight={500} py={4}>
                          {formatDistance(
                            addTimeZoneToTimestamp(account.updated_at),
                            new Date(),
                            {
                              addSuffix: true,
                              includeSeconds: true
                            }
                          )}
                          &nbsp;at&nbsp;
                          {format(
                            addTimeZoneToTimestamp(account.updated_at),
                            "h:mma"
                          )}
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {isLoadingCOAs && (
                <Center mt={10}>
                  <Spinner color="primary.500" />
                </Center>
              )}

              <Box mt={6}>
                <Paginate
                  page={coaPage}
                  data={chartOfAccounts}
                  handlePageClick={handleCOAPageClick}
                />
              </Box>
            </TableContainer>
          </Box>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
